import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { checksApi, ordersApi } from '../services/api';
import { useToast } from './Toast';
import { useOrderStore } from '../stores/orderStore';
import {
  Flame,
  Clock,
  CheckCircle2,
  ChefHat,
  Circle,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  Zap,
} from 'lucide-react';
import clsx from 'clsx';

interface CourseItem {
  id: string;
  name: string;
  quantity: number;
  status: string;
  holdUntilFired: boolean;
  sentAt: string | null;
  completedAt: string | null;
  seatNumber?: number;
}

interface Course {
  courseNumber: number;
  status: 'pending' | 'fired' | 'cooking' | 'ready' | 'served';
  items: CourseItem[];
  firedAt: string | null;
  servedAt: string | null;
}

interface CourseManagerProps {
  checkId: string;
  tableNumber?: string;
  onClose?: () => void;
  compact?: boolean;
}

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    icon: Circle,
    color: 'text-slate-400',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
  },
  fired: {
    label: 'Fired',
    icon: Flame,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  cooking: {
    label: 'Cooking',
    icon: ChefHat,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
  },
  ready: {
    label: 'Ready',
    icon: CheckCircle2,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
  },
  served: {
    label: 'Served',
    icon: CheckCircle2,
    color: 'text-slate-400',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
  },
};

export default function CourseManager({ checkId, tableNumber, onClose, compact = false }: CourseManagerProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [expandedCourses, setExpandedCourses] = useState<Set<number>>(new Set([1, 2, 3, 4, 5, 6]));
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Keep the local cart (order store) coherent with the server after firing:
  // the cart's "By Course" tab and Checkout render from the local store, so if
  // we fire here without resyncing, they keep showing stale WAITING/held state.
  const resyncLocalCart = () => {
    const store = useOrderStore.getState();
    if (store.checkId === checkId) {
      store.clearItems(); // empty store → views reload authoritative server data
    }
  };

  // Fetch course status
  const { data: courseData, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['courses', checkId],
    queryFn: async () => {
      const response = await checksApi.getCourses(checkId);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch courses');
      }
      return response.data as {
        courses: Course[];
        currentCourse: number;
        totalCourses: number;
      };
    },
    refetchInterval: 5000, // Refresh every 5 seconds
    enabled: !!checkId,
  });

  // Fire course mutation
  const fireCourseMutation = useMutation({
    mutationFn: async ({ courseNumber, isRush }: { courseNumber: number; isRush?: boolean }) => {
      const response = await ordersApi.fireCourse(checkId, courseNumber, isRush);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fire course');
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      showSuccess(`Course ${variables.courseNumber} fired to kitchen${variables.isRush ? ' (RUSH)' : ''}`);
      resyncLocalCart();
      queryClient.invalidateQueries({ queryKey: ['courses', checkId] });
      queryClient.invalidateQueries({ queryKey: ['check', checkId] });
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to fire course');
    },
  });

  // Fire specific items mutation
  const fireItemsMutation = useMutation({
    mutationFn: async ({ itemIds, isRush }: { itemIds: string[]; isRush?: boolean }) => {
      const response = await ordersApi.fireItems(itemIds, isRush);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fire items');
      }
      return response.data;
    },
    onSuccess: (data) => {
      showSuccess(`${data.itemsFired} item(s) fired to kitchen`);
      setSelectedItems(new Set());
      resyncLocalCart();
      queryClient.invalidateQueries({ queryKey: ['courses', checkId] });
      queryClient.invalidateQueries({ queryKey: ['check', checkId] });
    },
    onError: (error: Error) => {
      showError(error.message || 'Failed to fire items');
    },
  });

  const toggleCourse = (courseNumber: number) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(courseNumber)) {
      newExpanded.delete(courseNumber);
    } else {
      newExpanded.add(courseNumber);
    }
    setExpandedCourses(newExpanded);
  };

  const toggleItemSelection = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleFireSelectedItems = (isRush?: boolean) => {
    if (selectedItems.size === 0) return;
    fireItemsMutation.mutate({ itemIds: Array.from(selectedItems), isRush });
  };

  const formatTimeSince = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m ago`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 text-primary-600 animate-spin" />
      </div>
    );
  }

  const courses = courseData?.courses || [];
  const currentCourse = courseData?.currentCourse || 0;
  const totalCourses = courseData?.totalCourses || 0;

  // Items that can be fired = anything still held (holdUntilFired), regardless
  // of the course's display status. Using holdUntilFired as the single source
  // of truth keeps the badges, the Fire buttons, and the backend fire in sync.
  const pendingItems = courses
    .flatMap(c => c.items.filter(i => i.holdUntilFired));

  if (compact) {
    // Compact view for inline display
    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Course Progress</span>
            {totalCourses > 0 && (
              <span className="text-xs text-amber-600 font-medium">
                {currentCourse}/{totalCourses}
              </span>
            )}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="p-1 rounded hover:bg-slate-200"
          >
            <RefreshCw className={clsx('w-4 h-4 text-slate-400', isRefetching && 'animate-spin')} />
          </button>
        </div>

        {/* Compact Course Timeline */}
        <div className="p-3 space-y-2">
          {courses.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-2">No courses yet</p>
          ) : (
            courses.map((course) => {
              const config = STATUS_CONFIG[course.status];
              const StatusIcon = config.icon;
              const canFire = course.items.some(i => i.holdUntilFired);

              return (
                <div
                  key={course.courseNumber}
                  className={clsx(
                    'flex items-center justify-between p-2 rounded-lg border',
                    config.bgColor,
                    config.borderColor
                  )}
                >
                  <div className="flex items-center gap-2">
                    <StatusIcon className={clsx('w-4 h-4', config.color)} />
                    <span className="text-sm font-medium">Course {course.courseNumber}</span>
                    <span className={clsx('text-xs', config.color)}>
                      {config.label}
                    </span>
                  </div>
                  {canFire && (
                    <button
                      onClick={() => fireCourseMutation.mutate({ courseNumber: course.courseNumber })}
                      disabled={fireCourseMutation.isPending}
                      className="bg-orange-500 text-white text-xs font-medium px-2 py-1 rounded flex items-center gap-1 hover:bg-orange-600"
                    >
                      <Flame className="w-3 h-3" />
                      Fire
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-slate-600" />
              Course Manager
            </h3>
            <p className="text-sm text-slate-500">
              {tableNumber ? `Table ${tableNumber}` : 'Check'} • {totalCourses} course{totalCourses !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isRefetching}
              className="p-2 rounded-lg hover:bg-slate-100"
            >
              <RefreshCw className={clsx('w-5 h-5 text-slate-400', isRefetching && 'animate-spin')} />
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {totalCourses > 0 && (
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${(currentCourse / totalCourses) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-amber-600">
                {currentCourse} of {totalCourses}
              </span>
            </div>
          </div>
        )}

        {/* Course List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {courses.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No courses yet</p>
              <p className="text-sm">Add items to the order to create courses</p>
            </div>
          ) : (
            courses.map((course) => {
              const config = STATUS_CONFIG[course.status];
              const StatusIcon = config.icon;
              const isExpanded = expandedCourses.has(course.courseNumber);
              const canFire = course.items.some(i => i.holdUntilFired);
              const timeSinceFired = formatTimeSince(course.firedAt);

              return (
                <div
                  key={course.courseNumber}
                  className={clsx(
                    'rounded-xl border overflow-hidden',
                    config.borderColor
                  )}
                >
                  {/* Course Header */}
                  <div
                    className={clsx(
                      'px-4 py-3 flex items-center justify-between cursor-pointer',
                      config.bgColor
                    )}
                    onClick={() => toggleCourse(course.courseNumber)}
                  >
                    <div className="flex items-center gap-3">
                      <StatusIcon className={clsx('w-5 h-5', config.color)} />
                      <div>
                        <span className="font-semibold">Course {course.courseNumber}</span>
                        <div className="flex items-center gap-2 text-xs">
                          <span className={config.color}>{config.label}</span>
                          {timeSinceFired && course.status !== 'pending' && (
                            <span className="text-slate-400">• {timeSinceFired}</span>
                          )}
                          <span className="text-slate-400">
                            • {course.items.length} item{course.items.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canFire && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fireCourseMutation.mutate({ courseNumber: course.courseNumber, isRush: true });
                            }}
                            disabled={fireCourseMutation.isPending}
                            className="bg-red-500 text-white text-xs font-medium px-2 py-1.5 rounded-lg flex items-center gap-1 hover:bg-red-600"
                          >
                            <Zap className="w-3 h-3" />
                            Rush
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fireCourseMutation.mutate({ courseNumber: course.courseNumber });
                            }}
                            disabled={fireCourseMutation.isPending}
                            className="bg-orange-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-orange-600"
                          >
                            <Flame className="w-3 h-3" />
                            Fire
                          </button>
                        </>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Course Items */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 divide-y divide-slate-100">
                      {course.items.map((item) => (
                        <div
                          key={item.id}
                          className="px-4 py-2 flex items-center justify-between bg-white"
                        >
                          <div className="flex items-center gap-3">
                            {canFire && (
                              <input
                                type="checkbox"
                                checked={selectedItems.has(item.id)}
                                onChange={() => toggleItemSelection(item.id)}
                                className="w-4 h-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                              />
                            )}
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {item.quantity}x {item.name}
                              </p>
                              {item.seatNumber && (
                                <p className="text-xs text-slate-400">Seat {item.seatNumber}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {item.status === 'ready' || item.status === 'bumped' ? (
                              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                Ready
                              </span>
                            ) : item.status === 'cooking' ? (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                Cooking
                              </span>
                            ) : item.holdUntilFired ? (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                Not Sent
                              </span>
                            ) : (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                In Kitchen
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer - Fire Selected Items */}
        {selectedItems.size > 0 && (
          <div className="p-4 border-t border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleFireSelectedItems(true)}
                disabled={fireItemsMutation.isPending}
                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-red-600 disabled:opacity-50"
              >
                <Zap className="w-5 h-5" />
                Rush {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
              </button>
              <button
                onClick={() => handleFireSelectedItems(false)}
                disabled={fireItemsMutation.isPending}
                className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-orange-600 disabled:opacity-50"
              >
                <Flame className="w-5 h-5" />
                Fire {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
